import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { Typography } from "@kelp-dao/kernel-design-system";
import ResponsiveTooltip from "@components/Tooltip/ResponsiveTooltip";
import Image from "next/image";
import { getImgSrcFromStorage } from "@components/StorageImage";
import { HIGH_GROWTH_VAULT_STRINGS } from "../HighGrowthVault/constants";
import { StrategyAllocationType } from "@constants/types";
import { STARTEGY_REPORT_TIMERANGE_CHANGED } from "@constants/analytics";
import { trackEvent } from "utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

const TEXT_STRINGS = HIGH_GROWTH_VAULT_STRINGS.SECTIONS.PORTFOLIO.APR_REPORTS;

const REALIZED_APR_COLOR = "#259F76";
const ACCRUING_APR_COLOR = "#6E9BDE";

type Allocation = {
  allocation: string;
  key: string;
  strategy_name: string;
  img_url?: string;
  apr_on_allocation: string;
  apr: string;
  realized_apr: string;
  accruing_apr: string;
};

export type CMSAllocation = {
  strategy_start_date: string;
  strategy_end_date: string;
  allocations: Allocation[];
  note?: string;
  apy?: string;
  total_realized_apr?: string;
  total_accruing_apr?: string;
  total_apr_on_allocation?: string;
  detailed?: boolean;
};

const VaultAprReportsV2 = ({
  cmsAllocations,
  aprTitle,
  showDateSelector = true,
}: {
  cmsAllocations: CMSAllocation[];
  aprTitle?: string;
  showDateSelector?: boolean;
}) => {
  const dates = useMemo(() => {
    return [...cmsAllocations].reverse().map((data) => ({
      key: `${data.strategy_start_date}_${data.strategy_end_date}`,
      value: `${data.strategy_start_date} - ${data.strategy_end_date}`,
    }));
  }, [cmsAllocations]);

  // Initialize with first date to prevent flickering
  const [selectedDate, setSelectedDate] = useState(dates[0]?.key || "");

  useEffect(() => {
    if (dates.length > 0 && !selectedDate) {
      setSelectedDate(dates[0].key);
    }
  }, [dates, selectedDate]);

  const updatedAllocations = useMemo(() => {
    return cmsAllocations.reduce(
      (acc: Record<string, CMSAllocation[]>, curr) => {
        const key = `${curr.strategy_start_date}_${curr.strategy_end_date}`;
        if (acc[key]) {
          acc[key].push(curr);
        } else {
          acc[key] = [curr];
        }
        return acc;
      },
      {} as Record<string, CMSAllocation[]>
    );
  }, [cmsAllocations]);

  const selectedDateHistory = useMemo(() => {
    const allocationsData = updatedAllocations[selectedDate];
    if (allocationsData) {
      return {
        allocations: allocationsData?.[0]?.allocations,
        note: allocationsData?.[0]?.note || "",
        avgAPY: allocationsData?.[0]?.apy || "",
        total_realized_apr: allocationsData?.[0]?.total_realized_apr || "",
        total_accruing_apr: allocationsData?.[0]?.total_accruing_apr || "",
        total_apr_on_allocation:
          allocationsData?.[0]?.total_apr_on_allocation || "",
        detailed: allocationsData?.[0]?.detailed || false,
      };
    }
    return {
      allocations: [],
      note: "",
      avgAPY: "",
      total_realized_apr: "",
      total_accruing_apr: "",
      total_apr_on_allocation: "",
      detailed: false,
    };
  }, [updatedAllocations, selectedDate]);

  const {
    allocations,
    note,
    avgAPY,
    total_realized_apr,
    total_accruing_apr,
    total_apr_on_allocation,
  }: {
    allocations: StrategyAllocationType[];
    note: string;
    avgAPY: string;
    total_realized_apr: string;
    total_accruing_apr: string;
    total_apr_on_allocation: string;
  } = selectedDateHistory as any;

  const selectedDateObj = useMemo(() => {
    return dates.find((dateObj) => dateObj.key === selectedDate);
  }, [dates, selectedDate]);

  const hasDataForField = useCallback(
    (field: string) => {
      return allocations.some(
        (allocation) => allocation[field as keyof StrategyAllocationType]
      );
    },
    [allocations]
  );

  const visibleColumns = useMemo(() => {
    return TEXT_STRINGS.COLUMNS.filter((column) => {
      if (column.key === "realized_apr" || column.key === "accruing_apr") {
        return hasDataForField(column.key);
      }
      return true;
    });
  }, [hasDataForField]);

  const isNonZeroValue = (value: string | number): boolean => {
    return value !== "0" && value !== "0.00" && value !== 0;
  };

  return (
    <Flex
      direction={{ base: "column", md: "column", lg: "column" }}
      gap={{ base: "12px", md: "24px" }}
      alignItems={{ base: "stretch", md: "stretch", lg: "stretch" }}
      pt="4"
      pl={{ base: 0, md: "2" }}
      position="relative"
      overflow="visible"
    >
      {/* Header Row: Total APR on left, dropdown on right */}
      <Flex
        w="100%"
        alignItems="center"
        justifyContent="space-between"
        gap={{ base: "4px", md: "16px" }}
        position="relative"
        zIndex={1}
      >
        {/* Total APR Section */}
        <Flex
          w={{ base: "100%", md: "100%", lg: "auto" }}
          maxW={{ lg: "48%" }}
          direction="column"
        >
          <Flex
            p={{ base: 0, md: 0 }}
            w="100%"
            minH={{ base: "auto", md: "auto" }}
            align="flex-start"
            justify="flex-start"
            flex="1"
          >
            <Flex direction="column" align="flex-start" gap={1}>
              <Typography
                type="heading"
                variant="large"
                className="text-text-primary font-medium text-[20px] md:text-[24px] lg:text-[28px] leading-none font-sans"
              >
                {avgAPY}
                <span className="text-[20px] font-normal"> %</span>
              </Typography>
              <Flex gap="6px" align="center">
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary font-normal text-xs leading-[1.2]"
                >
                  Total {aprTitle ? aprTitle : "expected"} APR
                </Typography>
                {(total_realized_apr || total_accruing_apr) && (
                  <ResponsiveTooltip
                    title={`Total ${aprTitle ? aprTitle : "expected"} APR`}
                    content={
                      <Flex
                        direction="column"
                        w={{ base: "100%", md: "180px" }}
                        gap="6px"
                        p="5px"
                        fontWeight={500}
                      >
                        <Flex
                          alignItems="center"
                          justifyContent={"space-between"}
                        >
                          <Text fontSize={"12px"}>Realized APR</Text>
                          <Text color={REALIZED_APR_COLOR} fontSize={"18px"}>
                            {total_realized_apr
                              ? total_realized_apr + "%"
                              : "-"}
                          </Text>
                        </Flex>
                        <Flex
                          justifyContent={"space-between"}
                          alignItems="center"
                        >
                          <Text fontSize={"12px"}>Accruing APR</Text>
                          <Text color={ACCRUING_APR_COLOR} fontSize={"18px"}>
                            {total_accruing_apr
                              ? total_accruing_apr + "%"
                              : "-"}
                          </Text>
                        </Flex>
                      </Flex>
                    }
                    placement="bottom"
                    closeDelay={100}
                  />
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {/* Date dropdown on the right for all screen sizes */}
        {showDateSelector && (
          <Flex
            alignItems="center"
            justifyContent="flex-end"
            position="relative"
          >
            <Menu placement="bottom-end" strategy="fixed">
              <MenuButton
                as={Button}
                rightIcon={<FiChevronDown />}
                p={{ base: "4px 12px", md: "8px 12px" }}
                bg="white"
                borderRadius="8px"
                border="1px solid #cce5e5"
                _active={{ bg: "white" }}
                _hover={{ bg: "#f4f4f7" }}
                zIndex={1}
                position="relative"
              >
                <Text
                  fontSize={{ base: "12px", md: "14px" }}
                  lineHeight="18px"
                  fontWeight="400"
                  color="text.primary"
                >
                  {selectedDateObj?.value}
                </Text>
              </MenuButton>
              <MenuList
                py="12px"
                bg="white"
                borderRadius="8px"
                border="1px solid #cce5e5"
                minW="180px"
                boxShadow="0px 2px 4px 0px #075A5A3D"
                maxH="235px"
                overflowY="scroll"
                zIndex={1}
                position="relative"
              >
                {dates.map((data) => (
                  <MenuItem
                    key={data.key}
                    bg={data.key === selectedDate ? "#f4f4f7" : "white"}
                    py="6px"
                    px="10px"
                    onClick={() => {
                      setSelectedDate(data.key);
                      trackEvent(STARTEGY_REPORT_TIMERANGE_CHANGED, {
                        date_range: data.value,
                        date_key: data.key,
                      });
                    }}
                    _hover={{ bg: "#f4f4f7" }}
                  >
                    <Text
                      fontSize={{ base: "12px", md: "14px" }}
                      lineHeight="18px"
                      fontWeight={data.key === selectedDate ? "500" : "400"}
                      color="text.secondary"
                      _hover={{ color: "text.primary", fontWeight: "500" }}
                    >
                      {data.value}
                    </Text>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
        )}
      </Flex>

      {/* APR Table Section below header */}
      <Box display={{ base: "none", md: "block" }}>
        <Table variant="unstyled">
          <Thead>
            <Tr borderBottom="1px solid" borderColor="#cce5e5">
              {visibleColumns.map((field) => (
                <Th
                  key={field.key}
                  fontSize="12px"
                  lineHeight="14.4px"
                  fontWeight="400"
                  p={2}
                  textTransform="none"
                  color="text.secondary"
                  w={
                    !field.isNumeric
                      ? visibleColumns.length < 5
                        ? "45%"
                        : "35%"
                      : "inherit"
                  }
                >
                  <Flex gap="3px" flexShrink={"inherit"}>
                    <Flex>{field.name}</Flex>
                    {field.tooltipText && (
                      <Flex alignItems={"center"}>
                        <ResponsiveTooltip
                          title={field.name}
                          content={field.tooltipText}
                          placement="bottom"
                          closeDelay={100}
                        />
                      </Flex>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>

          <Tbody>
            {allocations.map((data, index: number) => (
              <Tr
                key={data.strategy_name}
                borderBottom={
                  allocations.length - 1 === index ? "1px solid" : "inherit"
                }
                borderColor={
                  allocations.length - 1 === index ? "#cce5e5" : "inherit"
                }
                color="black"
              >
                {visibleColumns.map((field) => (
                  <Td
                    key={field.key}
                    fontSize="12px"
                    lineHeight="14.4px"
                    fontWeight="400"
                    px={2}
                    py={4}
                    textTransform="none"
                    w={
                      !field.isNumeric
                        ? visibleColumns.length < 5
                          ? "45%"
                          : "35%"
                        : "inherit"
                    }
                    borderBottom="0px"
                  >
                    <Flex alignItems="center" gap="8px">
                      {field.hasImage && (data as any)["img_url"] && (
                        <Image
                          src={
                            (data as any).img_url.includes("http")
                              ? (data as any)["img_url"]
                              : getImgSrcFromStorage((data as any)["img_url"])
                          }
                          width={24}
                          height={24}
                          alt=""
                        />
                      )}
                      <Text
                        fontSize="14px"
                        fontWeight={
                          field.key == "accruing_apr" ||
                          field.key == "realized_apr"
                            ? "600"
                            : "400"
                        }
                        lineHeight="24px"
                        color={
                          field.key == "accruing_apr"
                            ? ACCRUING_APR_COLOR
                            : field.key == "realized_apr"
                            ? REALIZED_APR_COLOR
                            : "inherit"
                        }
                      >
                        {isNonZeroValue(
                          (data as any)[
                            field.key as keyof StrategyAllocationType
                          ]
                        )
                          ? `${
                              (data as any)[
                                field.key as keyof StrategyAllocationType
                              ]
                            } ${field.isNumeric ? "%" : ""}`
                          : "-"}
                      </Text>
                    </Flex>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
          {(total_realized_apr ||
            total_accruing_apr ||
            total_apr_on_allocation) && (
            <Tr px="0">
              {TEXT_STRINGS.COLUMNS.slice(0, -3).map((_, idx) => (
                <Td key={idx} borderBottom="0px"></Td>
              ))}
              {total_realized_apr && (
                <Td px="0" pl="2" borderBottom="0px">
                  <Text
                    color={REALIZED_APR_COLOR}
                    fontWeight="600"
                    fontSize={"16px"}
                  >
                    {total_realized_apr}%
                  </Text>
                </Td>
              )}
              {total_accruing_apr && (
                <Td px="0" pl="2" borderBottom="0px">
                  <Text
                    color={ACCRUING_APR_COLOR}
                    fontWeight="600"
                    fontSize={"16px"}
                  >
                    {total_accruing_apr}%
                  </Text>
                </Td>
              )}
              {total_apr_on_allocation && (
                <Td p="0" pl="2" borderBottom="0px">
                  <Text color="textPrimary" fontWeight="600" fontSize={"16px"}>
                    {total_apr_on_allocation}%
                  </Text>
                </Td>
              )}
            </Tr>
          )}
        </Table>
      </Box>

      {/* Mobile accordion table below header */}
      <Flex
        display={{ base: "inherit", md: "none" }}
        flexDir="column"
        gap="12px"
        alignItems="center"
      >
        <Accordion allowToggle w="100%">
          <Table variant="unstyled">
            <Thead borderBottom="1px solid" borderColor="#cce5e5">
              <Tr bg="inherit">
                <Th
                  fontSize="12px"
                  fontWeight="400"
                  p={2}
                  textTransform="none"
                  color="text.secondary"
                >
                  Strategy
                </Th>
                <Th
                  fontSize="10px"
                  fontWeight="400"
                  p={2}
                  textTransform="none"
                  isNumeric
                  textAlign="right"
                  color="text.secondary"
                >
                  APR on allocation
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {allocations.map((data) => (
                <AccordionItem
                  key={data.strategy_name}
                  display={"contents"}
                  border="none"
                >
                  {({ isExpanded }) => (
                    <>
                      <Tr bg="inherit" color="black">
                        <AccordionButton
                          bg="transparent"
                          _hover={{ bg: "transparent" }}
                          display={"contents"}
                        >
                          <Td fontSize="10px" fontWeight="400" p={2}>
                            <Flex alignItems="center" gap="8px">
                              {(data as any).img_url && (
                                <Image
                                  src={
                                    (data as any).img_url.includes("http")
                                      ? (data as any).img_url
                                      : getImgSrcFromStorage(
                                          (data as any).img_url
                                        )
                                  }
                                  width={24}
                                  height={24}
                                  alt=""
                                />
                              )}
                              <Text color="inherit">
                                {(data as any).strategy_name}
                              </Text>
                            </Flex>
                          </Td>
                          <Td
                            fontSize="10px"
                            fontWeight="400"
                            p={2}
                            isNumeric
                            verticalAlign={"middle"}
                            textAlign="right"
                          >
                            <Text color="inherit">
                              {isNonZeroValue((data as any).apr_on_allocation)
                                ? (data as any).apr_on_allocation + "%"
                                : "-"}
                              <AccordionIcon pl={"2px"} />
                            </Text>
                          </Td>
                        </AccordionButton>
                      </Tr>
                      {isExpanded && (
                        <Tr color="black">
                          <Td colSpan={3} p={0} border="none">
                            <AccordionPanel
                              p={4}
                              pt="2"
                              borderTop="1px solid"
                              borderColor="#cce5e5"
                            >
                              <Flex flexDir="column" gap="2px" pt="8px">
                                <Flex
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Text
                                    fontSize="8px"
                                    color="inherit"
                                    fontWeight="400"
                                  >
                                    Allocation %
                                  </Text>
                                  <Text
                                    fontSize="8px"
                                    color="inherit"
                                    fontWeight="500"
                                  >
                                    {isNonZeroValue((data as any).allocation)
                                      ? (data as any).allocation + "%"
                                      : "-"}
                                  </Text>
                                </Flex>
                                <Flex
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Flex alignItems="center" gap="4px">
                                    <Text
                                      fontSize="8px"
                                      color="inherit"
                                      fontWeight="400"
                                    >
                                      Total APR
                                    </Text>
                                    <Box as="span">
                                      <ResponsiveTooltip
                                        title="Total APR"
                                        content={
                                          TEXT_STRINGS.COLUMNS.find(
                                            (c: any) =>
                                              c.key === "apr_on_allocation"
                                          )?.tooltipText || ""
                                        }
                                        placement="bottom"
                                        closeDelay={100}
                                      />
                                    </Box>
                                  </Flex>
                                  <Text
                                    fontSize="8px"
                                    color="inherit"
                                    fontWeight="500"
                                  >
                                    {isNonZeroValue(
                                      (data as any).apr_on_allocation
                                    )
                                      ? (data as any).apr_on_allocation + "%"
                                      : "-"}
                                  </Text>
                                </Flex>
                                {(hasDataForField("realized_apr") ||
                                  hasDataForField("accruing_apr")) && (
                                  <Flex
                                    justifyContent="space-between"
                                    alignItems="center"
                                    pl={"10px"}
                                  >
                                    {hasDataForField("realized_apr") && (
                                      <>
                                        <Flex alignItems="center" gap="8px">
                                          <Text
                                            fontSize="8px"
                                            color="inherit"
                                            fontWeight="400"
                                          >
                                            Realized APR
                                          </Text>
                                          <Box as="span">
                                            <ResponsiveTooltip
                                              title="Realized APR"
                                              content={
                                                TEXT_STRINGS.COLUMNS.find(
                                                  (c: any) =>
                                                    c.key === "realized_apr"
                                                )?.tooltipText || ""
                                              }
                                              placement="bottom"
                                              closeDelay={100}
                                            />
                                          </Box>
                                        </Flex>
                                        <Text
                                          fontSize="8px"
                                          color={REALIZED_APR_COLOR}
                                          fontWeight="500"
                                        >
                                          {isNonZeroValue(
                                            (data as any).realized_apr
                                          )
                                            ? (data as any).realized_apr + "%"
                                            : "-"}
                                        </Text>
                                      </>
                                    )}
                                  </Flex>
                                )}
                                <Flex
                                  justifyContent="space-between"
                                  alignItems="center"
                                  pl={"10px"}
                                >
                                  {hasDataForField("accruing_apr") && (
                                    <>
                                      <Flex alignItems="center" gap="8px">
                                        <Text
                                          fontSize="8px"
                                          color="inherit"
                                          fontWeight="400"
                                        >
                                          Accruing APR
                                        </Text>
                                        <Box as="span">
                                          <ResponsiveTooltip
                                            title="Accruing APR"
                                            content={
                                              TEXT_STRINGS.COLUMNS.find(
                                                (c: any) =>
                                                  c.key === "accruing_apr"
                                              )?.tooltipText || ""
                                            }
                                            placement="bottom"
                                            closeDelay={100}
                                          />
                                        </Box>
                                      </Flex>
                                      <Text
                                        fontSize="8px"
                                        color={ACCRUING_APR_COLOR}
                                        fontWeight="500"
                                      >
                                        {isNonZeroValue(
                                          (data as any).accruing_apr
                                        )
                                          ? (data as any).accruing_apr + "%"
                                          : "-"}
                                      </Text>
                                    </>
                                  )}
                                </Flex>
                              </Flex>
                            </AccordionPanel>
                          </Td>
                        </Tr>
                      )}
                    </>
                  )}
                </AccordionItem>
              ))}
            </Tbody>
          </Table>
        </Accordion>
      </Flex>

      {note && (
        <Flex>
          <Typography
            type="body"
            variant="xsmall"
            className="text-[10px] tablet:text-xs text-text-secondary leading-5 whitespace-pre-line font-normal"
          >
            <span className="font-semibold">Note:</span> {note}
          </Typography>
        </Flex>
      )}
    </Flex>
  );
};

export default VaultAprReportsV2;
